
import { UntypedFormControl } from "@angular/forms";
import { FieldType, FieldTypeConfig, FormlyField, FormlyFieldConfig, FormlyFieldProps } from "@ngx-formly/core";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Type } from "@angular/core";
import { AppFileViewerService } from "@intellio/shared/services";
import { ApplicationFacade } from "@intellio/shared/state-mgmt";
import { combineLatest, map, Subject, take, takeUntil } from "rxjs";

interface ViewFileButtonProps extends FormlyFieldProps {
    filePath;
    showMissingFileState?: boolean;
}

export interface FormlyViewFileButtonConfig extends FormlyFieldConfig<ViewFileButtonProps> {
    type: 'button' | Type<ViewFileButtonComponent>
}

@Component({
    selector: 'view-file-button',
    styleUrls: ['./view-file-button.component.scss'],
    template: `
        <div class="formly-field-button">
            <button
                type="button"
                (click)="onClick()"
                [disabled]="!initialized || loadingError || fileMissing"
                >{{getButtonText()}}</button>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewFileButtonComponent
    extends FieldType<FieldTypeConfig>
    implements OnInit, OnDestroy, AfterViewInit
{
    private destroyed$ = new Subject<void>();
    control: UntypedFormControl;
    isOpen: boolean = false;
    fileName: string = null;
    appId: string = null;
    initialized = false;
    blob:Blob = null;
    blobUrl: any;
    loadingError = false;
    fileMissing = false;

    constructor(
        private appFileViewerService: AppFileViewerService,
        private appFacade: ApplicationFacade,
        private cd: ChangeDetectorRef
    ) {
        super();
        this.isOpen = false;
    }
    ngAfterViewInit(): void {
    }
    ngOnDestroy(): void {
        if(this.blobUrl){
            this.blob = null;
            URL.revokeObjectURL(this.blobUrl);
        }

        if(this.appFileViewerService.isVisible){
            this.appFileViewerService.hide();
        }

        this.destroyed$.next();
        this.destroyed$.complete();
    }

    getButtonText() {
        if(this.fileMissing){
            return 'File Missing'
        }

        if(!this.initialized){
            return 'Loading File Preview'
        }

        if(this.loadingError){
            return 'Unable to find file'
        }

        if(this.isOpen){
            return 'Close File'
        }

        if(!this.isOpen){
            return 'Open File'
        }

        return 'Error with file'
    }

    ngOnInit() {
        this.control = this?.formControl as UntypedFormControl;
        this.appFileViewerService.visibleChanges$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((isVisible) => {
                if (!isVisible && this.isOpen) {
                    this.isOpen = false;
                    this.cd.markForCheck();
                }
            });
        this.init();
    }



    init(){
        var app$ = this.appFacade.currentApplication$;
        var intActResults$ = this.appFacade.applicationIntelligentActionResults$;
        combineLatest([app$, intActResults$]).pipe(
            takeUntil(this.destroyed$),
        ).subscribe(
            {
                next: ([app, results]) => {
                    this.appId = app.id;
                    var rule = this.props.rule;
                    var instanceId = this.props.instanceId;
                    if(!rule || instanceId === undefined || instanceId === null){
                        return;
                    }
                    if(results === null){
                        if(this.props.showMissingFileState){
                            this.fileMissing = false;
                            this.initialized = false;
                            this.cd.markForCheck();
                        }
                        return;
                    }
                    var result = results.find(r =>
                        r.ruleName === rule &&
                        (!this.props.showMissingFileState || r.instanceId.toString() === instanceId.toString())
                    );
                    const associatedFile = result?.associatedFile?.trim();
                    if(associatedFile){
                        var fileName = associatedFile;

                        if (!fileName.toLowerCase().endsWith('.pdf')) {
                            const lastExtensionIndex = fileName.lastIndexOf('.');
                            if (lastExtensionIndex !== -1) {
                                fileName = fileName.substring(0, lastExtensionIndex) + '.pdf';
                            } else {
                                fileName = fileName + '.pdf';
                            }
                        }

                        this.fileName = fileName;
                        this.loadingError = false;
                        this.fileMissing = false;
                        this.initialized = true;
                        this.cd.markForCheck();
                    } else if(this.props.showMissingFileState){
                        this.fileName = null;
                        this.fileMissing = true;
                        this.initialized = true;
                        this.cd.markForCheck();
                    }
                },
            }
        );

    }

    onClick() {
        if(!this.initialized || this.fileMissing){
            return;
        }
        if(!this.blob){
            this.appFileViewerService.getIntelligentActionFile(this.appId, this.fileName)
            .pipe(
                take(1),
            )
            .subscribe(
                {
                    next: (preview: Blob) => {
                        this.blob = preview;
                        this.blobUrl = URL.createObjectURL(preview);
                        this.showPreview();
                    },
                    error: () => {
                        this.loadingError = true;
                        this.cd.markForCheck();
                    }
                }
            )
        } else {
            if(!this.isOpen){
                this.showPreview();
            } else {
                this.appFileViewerService.hide();
                this.isOpen = false;
                this.cd.markForCheck();
            }
        }
    }

    private showPreview(): void {
        if (this.appFileViewerService.isVisible) {
            this.appFileViewerService.hide();
        }

        this.appFileViewerService.setFile(this.blobUrl);
        this.appFileViewerService.show();
        this.isOpen = true;
        this.cd.markForCheck();
    }
}
